// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { InputRow, ResultCard, PremiumLock, fmt } from './shared';

const CONSAPCalc = () => {
  const [eta, setEta] = useState('30');
  const [prezzo, setPrezzo] = useState('200000');
  const [anticipo, setAnticipo] = useState('20000');
  const [isee, setIsee] = useState('30000');
  const [tipoLav, setTipoLav] = useState('di_ti');
  const [primaCasa, setPrimaCasa] = useState(true);

  const P = parseFloat(prezzo) || 0;
  const A = parseFloat(anticipo) || 0;
  const ISEE_V = parseFloat(isee) || 0;
  const ETa = parseInt(eta) || 30;
  const ltvPct = P > 0 ? (A / P) * 100 : 0;
  const needsConsap = A / P < 0.20 && primaCasa;
  const isPrioritaria = ETa < 36 && ISEE_V <= 40000;
  const risparmioStimato = needsConsap ? Math.round(0.003 * (P - A) * 25) : 0;

  const ltvColor = ltvPct >= 20 ? '#3D7A52' : ltvPct >= 10 ? '#C9A84C' : '#E24B4A';
  const ltvLabel = ltvPct >= 20 ? '✓ Sufficiente' : ltvPct >= 10 ? '⚠ Borderline' : '⚠ Insufficiente';

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ position: 'relative' }}>
      <PremiumLock />
      <div style={{ background: 'rgba(201,168,76,0.06)', borderLeft: '3px solid #C9A84C', borderRadius: '0 8px 8px 0', padding: '14px 18px', marginBottom: 20, fontSize: 13, color: '#888780', lineHeight: 1.6 }}>
        <strong style={{ color: '#C9A84C' }}>Il Fondo di Garanzia Prima Casa CONSAP</strong> permette allo Stato di garantire fino all'80% del mutuo. Scopri se hai diritto alla garanzia e quanto potresti risparmiare sul costo totale del mutuo.
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <InputRow label="Età" value={eta} onChange={setEta} suffix="anni" tooltip="Under 36 hanno priorità e agevolazioni fiscali aggiuntive (esenzione imposta registro)" />
        <InputRow label="Prezzo immobile (€)" value={prezzo} onChange={setPrezzo} prefix="€" />
        <InputRow label="Risparmio disponibile per anticipo (€)" value={anticipo} onChange={setAnticipo} prefix="€" />
        <InputRow label="Reddito ISEE (€)" value={isee} onChange={setIsee} prefix="€" tooltip="Necessario per le categorie prioritarie. Puoi ottenerlo gratuitamente presso un CAF." />
        <div>
          <label className="text-[#888780] text-sm block mb-1">Tipo lavoratore</label>
          <select value={tipoLav} onChange={e => setTipoLav(e.target.value)} className="w-full bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md px-3 py-2 text-sm">
            <option value="di_ti">Dipendente tempo indeterminato</option>
            <option value="di_td">Dipendente tempo determinato</option>
            <option value="autonomo">Autonomo</option>
            <option value="altro">Altro</option>
          </select>
        </div>
        <div>
          <label className="text-[#888780] text-sm block mb-1">Prima casa</label>
          <div className="flex gap-3 mt-2">
            {[true, false].map(v => (
              <button key={String(v)} onClick={() => setPrimaCasa(v)}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{ background: primaCasa === v ? '#C9A84C' : 'rgba(255,255,255,0.04)', color: primaCasa === v ? '#3A2800' : '#888780', border: `1px solid ${primaCasa === v ? '#C9A84C' : 'rgba(255,255,255,0.08)'}` }}>
                {v ? 'Sì' : 'No'}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <div className="p-4 rounded-xl" style={{ background: '#252523', border: `1px solid ${ltvColor}40` }}>
          <p className="text-xs text-[#888780] mb-1">Anticipo disponibile</p>
          <p className="text-2xl font-bold" style={{ color: ltvColor }}>{ltvPct.toFixed(1)}%</p>
          <p className="text-xs mt-1" style={{ color: ltvColor }}>{ltvLabel}</p>
        </div>
        <div className="p-4 rounded-xl" style={{ background: needsConsap ? 'rgba(61,122,82,0.08)' : '#252523', border: needsConsap ? '1px solid rgba(61,122,82,0.4)' : '1px solid rgba(255,255,255,0.06)' }}>
          <p className="text-xs text-[#888780] mb-1">Accesso CONSAP</p>
          {needsConsap ? (
            <><p className="text-sm font-semibold" style={{ color: '#3D7A52' }}>✓ Potenzialmente idoneo</p><p className="text-xs mt-1 text-[#888780]">Hai meno del 20% di anticipo sulla prima casa</p></>
          ) : !primaCasa ? (
            <><p className="text-sm font-semibold text-[#888780]">Non applicabile</p><p className="text-xs mt-1 text-[#888780]">Solo prima casa</p></>
          ) : (
            <><p className="text-sm font-semibold text-[#888780]">Non necessario</p><p className="text-xs mt-1 text-[#888780]">Hai già il 20% di anticipo</p></>
          )}
        </div>
        <div className="p-4 rounded-xl" style={{ background: isPrioritaria ? 'rgba(61,122,82,0.08)' : '#252523', border: isPrioritaria ? '1px solid rgba(61,122,82,0.4)' : '1px solid rgba(255,255,255,0.06)' }}>
          <p className="text-xs text-[#888780] mb-1">Categoria prioritaria</p>
          {isPrioritaria ? (
            <><p className="text-sm font-semibold" style={{ color: '#3D7A52' }}>✓ Categoria prioritaria</p><p className="text-xs mt-1 text-[#888780]">Under 36 + ISEE {'<'}€40.000 → accesso facilitato + agevolazioni fiscali</p></>
          ) : (
            <><p className="text-sm font-semibold text-[#888780]">Accesso standard</p><p className="text-xs mt-1 text-[#888780]">Puoi comunque richiedere il CONSAP</p></>
          )}
        </div>
        <div className="p-4 rounded-xl" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)' }}>
          <p className="text-xs text-[#888780] mb-1">Risparmio stimato con CONSAP</p>
          <p className="text-2xl font-bold text-[#C9A84C]">{needsConsap ? `€${fmt(risparmioStimato)}` : '—'}</p>
          <p className="text-xs mt-1 text-[#888780]">Risparmio stimato sullo spread in 25 anni</p>
        </div>
      </div>

      <div className="p-4 rounded-xl text-sm leading-relaxed" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.25)', color: '#888780' }}>
        <strong style={{ color: '#C9A84C' }}>⚠ Il CONSAP non è garantito automaticamente.</strong> La banca deve aderire al fondo e valutare comunque il tuo profilo creditizio. Informazioni ufficiali: <strong>consap.it</strong> — Puoi fare domanda tramite qualsiasi banca aderente al fondo.
      </div>
</motion.div>
  );
};

export default CONSAPCalc;
